import { Button, Typography } from '@mui/material';
import { AutocompleteAdapter } from 'components/enhanced/form/adapters/selects/AutocompleteAdapter';
import { TextFieldAdapter } from 'components/enhanced/form/adapters/text-inputs/TextFieldAdapter';
import EnhancedForm from 'components/enhanced/form/EnhancedForm';
import { IdentityField } from 'components/enhanced/form/fields/IdentityField';
import { composeValidators, required } from 'components/helpers/validators';
import { IProduct } from 'features/inventory/products/types';
import { stockQuantityLocationService } from 'features/inventory/stock/api/stock-quantity-location.service';
import { stockQuantityService } from 'features/inventory/stock/api/stock-quantity.service';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { productService } from 'shared/services';
import { Option } from 'shared/types/general';
import { transportDocumentActiveLineService } from '../../api/transport-active-document-line.service';

interface TransportActiveDocumentLineFormProps {
  ddt;
  onSubmit: any;
  products: Option[];
  setProduct: any;
}

const TransportActiveDocumentLineForm = (props: TransportActiveDocumentLineFormProps) => {
  const { ddt, onSubmit, products, setProduct } = props;

  const translationPrefix = 'pages.active-ddt.new-product';
  const { t } = useTranslation();
  const [maxUnits, setMaxUnits] = useState<number | null>(null);
  const [consumedUnits, setConsumedUnits] = useState<number>(0);
  const [selectedBatch, setSelectedBatch] = useState<Option | null>(null);

  const [batchesOption, setBatchesOptions] = useState<Option[]>([]);
  const [selectedProduct, setSelectedProduct] = useState<IProduct>();

  // const [updateContext, setUpdateContext] = useState(false);

  useEffect(() => {
    setProduct(selectedProduct);
    if (selectedProduct) {
      const params = {
        product__id: selectedProduct.id ?? '',
        warehouse__id: ddt.warehouse_out ?? '',
        quantity__gt: 0
      };

      // ------------------------- costrutto per la gestione "COMBINATA" di StockQuantity & StockQuantityLocation -------------------------
      // Prima chiamata API
      stockQuantityService
        .getUnloadStocks(1000, 0, params)
        .then((res) => {
          const firstBatchData = res.results
            .filter((value) => value.free_quantity > 0)
            .map((value) => {
              let description = value.product_batch;
              if (value.expiration_date) description += `; Scad: ${value.expiration_date}`;
              description += `; Qty: ${value.free_quantity}`;
              return {
                id: crypto.randomUUID(),
                code: description,
                product_batch: value.product_batch,
                expiration_date: value.expiration_date,
                qty: value.free_quantity
              };
            });

          // Aggiornamento dello stato iniziale
          setBatchesOptions(firstBatchData);

          // Seconda chiamata API (una volta completata la prima)
          return stockQuantityLocationService.getUnloadStockLocation(1000, 0, params);
        })
        .then((res) => {
          const secondBatchData = res.results.map((value) => {
            let description = value.product_batch;
            if (value.expiration_date) description += `; Scad: ${value.expiration_date}`;
            description += `; Qty: ${value.quantity}; ${value.location__code}`;
            return {
              id: crypto.randomUUID(),
              code: description,
              product_batch: value.product_batch,
              expiration_date: value.expiration_date,
              location: value.location__code,
              qty: value.quantity
            };
          });

          // Aggiornamento dello stato combinando i dati di entrambe le chiamate
          setBatchesOptions((prevBatches) => [...prevBatches, ...secondBatchData]);
        });
    } else {
      setBatchesOptions([]);
    }
  }, [selectedProduct]);

  useEffect(() => {
    updateQty();
  }, [selectedBatch]);

  const updateQty = (value?: any) => {
    if (selectedBatch) {
      let qty = selectedBatch['qty'];

      ddt.lines.forEach((line) => {
        let productId;
        if (typeof line.product === 'object' && line.product !== null) {
          productId = line.product.id;
        } else {
          productId = line.product;
        }
        if (line.product_batch === selectedBatch['id'] && productId === selectedProduct.id) {
          qty -= line.quantity;
        }
      });

      if (value) {
        if (
          value.product.id === selectedProduct.id &&
          value.product_batch.id === selectedBatch['id']
        ) {
          qty -= value.quantity;
        }
      }

      const matchedBatch: any = batchesOption.find((batch) => batch.id === selectedBatch['id']);

      transportDocumentActiveLineService
        .getAlreadySelectedProducts({
          product: selectedProduct.id,
          product_batch: matchedBatch ? matchedBatch.product_batch : '',
          location: matchedBatch ? matchedBatch.location : ''
        })
        .then((res) => {
          setMaxUnits(qty);
          setConsumedUnits(res.selected_quantity);
        });
    }
  };

  const handleOnSubmit = (values: any) => {
    onSubmit({ ...values });
    setSelectedProduct(null);
    updateQty(values);
  };

  return (
    <div className="flex flex-wrap">
      <div className="w-full p-2">
        <EnhancedForm
          onSubmit={handleOnSubmit}
          formId="addTransportActiveDocumentLine"
          autoStyleLayout={true}
          columns={2}
          costumControls={
            <Button
              type="submit"
              form="addTransportActiveDocumentLine"
              variant="contained"
              className="w-full">
              {t(`${translationPrefix}.add_line`)}
            </Button>
          }>
          <IdentityField
            name="product"
            component={AutocompleteAdapter}
            options={products}
            label={t(`${translationPrefix}.product`)}
            optionLabel="description"
            type="text"
            validate={composeValidators(required)}
            setValue={setSelectedProduct}
            getMethodService={productService}
            required
            submitObject
          />
          <IdentityField
            key={selectedProduct}
            name="product_batch"
            label={t(`${translationPrefix}.product_batch_expiration_date`)}
            component={AutocompleteAdapter}
            options={batchesOption}
            type="text"
            validate={required}
            required
            setValue={setSelectedBatch}
            autoSelect={false}
            submitObject
          />
          {maxUnits !== null && (
            <Typography color="text-primary-600" style={{ gridColumn: '1 / -1' }}>
              <b>{`${t(`${translationPrefix}.aviable_units`, {
                qty: maxUnits - consumedUnits
              })}`}</b>
            </Typography>
          )}
          <IdentityField
            gridSpan={2}
            name="quantity"
            component={TextFieldAdapter}
            label={t(`${translationPrefix}.quantity`)}
            type="number"
          />
        </EnhancedForm>
      </div>
    </div>
  );
};

export default TransportActiveDocumentLineForm;
